import React, { useEffect, useState, useRef } from 'react';
import { useParticles } from '../../contexts/ParticleContext';

export const EMOTES = {
    PIECE: { id: 'PIECE', icon: '✌️', sprite: 'piece_anim', color: '#86efac' },
    SMIRK: { id: 'SMIRK', icon: '😏', sprite: 'smirk_anim', color: '#fcd34d' },
    ANGER: { id: 'ANGER', icon: '🤬', sprite: 'anger_anim', color: '#ef4444' },
    SAD: { id: 'SAD', icon: '😢', sprite: 'sad_anim', color: '#60a5fa' },
    SHOCKED: { id: 'SHOCKED', icon: '😱', sprite: 'shock_anim', color: '#c084fc' },
    THANKS: { id: 'THANKS', icon: '🙏', sprite: 'thanks_anim', color: '#f472b6' },
} as const;

export type EmoteId = keyof typeof EMOTES;

interface EmoteBubbleProps {
    emoteId: string;
    type: 'premium' | 'animated';
    isOpponent: boolean;
    onComplete: () => void;
    avatarRect?: { x: number, y: number, w: number, h: number }; // For sprite positioning
}

export const EmoteBubble: React.FC<EmoteBubbleProps> = ({
    emoteId, type, isOpponent, onComplete, avatarRect
}) => {
    const particleLayerRef = useParticles();
    const [isVisible, setIsVisible] = useState(true);
    const emote = Object.values(EMOTES).find(e => e.id === emoteId);

    // Guard to prevent multiple sprite spawns
    const hasSpawnedRef = useRef(false);
    // Capture initial avatarRect to prevent re-triggering on hover
    const initialAvatarRectRef = useRef(avatarRect);

    useEffect(() => {
        // Reset spawn guard when emote changes
        hasSpawnedRef.current = false;
        initialAvatarRectRef.current = avatarRect;
    }, [emoteId]);

    useEffect(() => {
        const rect = initialAvatarRectRef.current;

        if (type === 'animated' && particleLayerRef && rect) {
            // Guard: only spawn once per emote activation
            if (hasSpawnedRef.current) return;
            hasSpawnedRef.current = true;

            setIsVisible(false); // Hide the bubble if using particle layer

            const spriteUrl = `/assets/emotes/animated/${emoteId.toLowerCase()}.png`;
            const img = new Image();
            img.src = spriteUrl;

            img.onload = () => {
                const h = img.naturalHeight;
                const w = img.naturalWidth;
                if (h === 0) return;

                const frameSize = h;
                const frames = Math.floor(w / h);

                const EMOTE_FPS_CONFIG: Record<string, number> = {
                    ANGER: 3, PIECE: 2, SAD: 2, SMIRK: 3, SHOCKED: 2, THANKS: 2,
                };
                let fps = EMOTE_FPS_CONFIG[emoteId] || 2;

                const yOffset = isOpponent ? 50 : -50;

                particleLayerRef.spawnSprite(
                    rect.x + rect.w / 2,
                    rect.y + rect.h / 2 + yOffset,
                    {
                        spriteUrl: spriteUrl,
                        frameWidth: frameSize,
                        frameHeight: frameSize,
                        totalFrames: frames,
                        fps: fps,
                        loop: false,
                        scale: 1.2
                    }
                );
            };

            const timer = setTimeout(onComplete, 2000);
            return () => clearTimeout(timer);
        } else {
            // Standard or Premium (PNG) Bubble
            setIsVisible(true);
            const timer = setTimeout(() => {
                setIsVisible(false);
                setTimeout(onComplete, 300);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [emoteId, type]);

    if (!emote || !isVisible) return null;

    return (
        <div
            className={`absolute left-1/2 -translate-x-1/2 z-50 flex items-center justify-center
        transition-all duration-300 animate-bounce-in
        ${isOpponent ? '-bottom-16' : '-top-16'}
      `}
            style={{
                transformOrigin: isOpponent ? 'top center' : 'bottom center'
            }}
        >
            {/* Chat Bubble Tail */}
            <div className={`absolute w-4 h-4 bg-white rotate-45 border-r-2 border-b-2 border-slate-700
            ${isOpponent ? '-top-2 bg-slate-100' : '-bottom-2 bg-white'}
        `}></div>

            {/* Bubble Body */}
            <div className="relative bg-white border-2 border-slate-700 rounded-2xl p-1 shadow-xl min-w-[80px] text-center overflow-hidden">
                <img
                    src={`/assets/emotes/${emoteId.toLowerCase()}.png`}
                    alt={emoteId}
                    className="w-16 h-16 object-contain mx-auto"
                />
            </div>
        </div>
    );
};
